import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Package } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import apiService from '@/services/api';

interface Order {
  id: number;
  service_name?: string;
  property_name?: string;
  postcode?: string;
  total_amount_pence: number;
  status: string;
  booking_status?: string;
  created_at: string;
}

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrderHistory = async () => {
      try {
        const response = await apiService.request('/customer/orders', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const pastOrders = response.data.filter(
            (order: Order) => (order.booking_status || order.status) === 'completed' || (order.booking_status || order.status) === 'cancelled'
          );
          setOrders(pastOrders);
        }
      } catch (error) {
        console.error('Failed to fetch order history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderHistory();
  }, []);

  return (
    <>
      <Helmet>
        <title>Order History | Virewire Property Services</title>
        <meta name="description" content="View your order history in your Virewire customer dashboard." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          <h1 className="text-4xl font-bold text-[#00543b] mb-8">Order History</h1>

          <Card>
            <CardHeader>
              <CardTitle>Your Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00543b]" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg mb-2">No orders yet</p>
                  <p className="text-sm">Your past orders will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => {
                    const amountGBP = ((order.total_amount_pence ?? 0) / 100).toFixed(2);
                    const status = order.booking_status || order.status;
                    return (
                      <div
                        key={order.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/order/${order.id}`)}
                      >
                        <div>
                          <p className="text-xs font-bold text-gray-600 uppercase mb-1">#ORD-{order.id}</p>
                          <p className="font-semibold text-gray-900">{order.service_name || 'Service'}</p>
                          <p className="text-sm text-gray-600">{order.property_name || order.postcode || 'Property'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#00543b] text-lg mb-1">£{amountGBP}</p>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                            status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {status?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default OrderHistoryPage;
